'use client';

import ImageGrid from '@/components/blocks/image-grid/image-grid';
import TextRight from '@/components/blocks/text/text-right';
import H1 from '@/components/typography/h1';
import H4 from '@/components/typography/h4';
import Label from '@/components/typography/label';
import Quote from '@/components/typography/quote';
import { RichText } from '@/components/ui/rich-text';
import { localizedRoutes } from '@/lib/i18n-routes';
import { cn, formatDate } from '@/lib/utils';
import { Artikelen } from '@/payload-types';
import Image from 'next/image';
import { useParams } from 'next/navigation';

type ArticleDetailProps = {
    article: Artikelen;
};

export default function ArticleDetail({ article }: ArticleDetailProps) {
    const { locale } = useParams() as {
        locale: keyof (typeof localizedRoutes)['article'];
    };
    const formattedDate = formatDate(article.updatedAt, locale);

    return (
        <div>
            <div className="flex flex-col gap-8 px-5 pt-[5rem] lg:mx-auto lg:flex-row lg:justify-center lg:pt-[13rem]">
                <div className="flex items-center justify-end whitespace-nowrap lg:mt-3 lg:mb-[2.5rem] lg:w-1/4 lg:items-start lg:justify-center">
                    <Label>{formattedDate}</Label>
                </div>
                <div className="space-x-2 lg:max-w-[1120px] lg:space-x-0">
                    <H1 className="mb-[2rem] lg:hidden">{article.titel}</H1>
                    <Quote className="mb-[2rem] hidden lg:block lg:max-w-5/6">
                        {article.titel}
                    </Quote>
                    <H4 className="mb-[2rem] text-[20px]! leading-[1.20] normal-case lg:text-[24px]!">
                        <RichText data={article.introductie} />
                    </H4>
                </div>
            </div>
            <div className="mt-10 px-2 lg:mt-30 lg:px-5">
                <Image
                    src={
                        typeof article.banner === 'string'
                            ? '/placeholder.svg'
                            : article.banner?.url || '/placeholder.svg'
                    }
                    alt={
                        typeof article.banner === 'string'
                            ? ''
                            : article.banner.alt
                    }
                    width={3760}
                    height={1300}
                    className={cn(
                        'bg-accent h-[323px] w-full rounded-lg object-cover lg:h-[650px]',
                        { 'object-fill': typeof article.banner === 'string' },
                    )}
                />
            </div>
            <div className="border-primary flex flex-col gap-10 border-b pt-10 pb-[6rem] lg:gap-20 lg:pt-20 lg:pb-[11.5rem]">
                {article.content.map((block, index) => {
                    switch (block.blockType) {
                        case 'text-right':
                            return (
                                <TextRight
                                    className="max-w-[1120px]"
                                    key={index}
                                    titel={block.titel}
                                    tekst={block.tekst}
                                    bijlage={block.bijlage}
                                />
                            );
                        case 'image-grid-2x2':
                            return (
                                <ImageGrid
                                    key={index}
                                    images={[
                                        { afbeelding: block.afbeelding1 },
                                        { afbeelding: block.afbeelding2 },
                                        { afbeelding: block.afbeelding3 },
                                        { afbeelding: block.afbeelding4 },
                                    ]}
                                />
                            );
                        case 'image-grid-2x1':
                            return (
                                <ImageGrid
                                    key={index}
                                    images={[
                                        { afbeelding: block.afbeelding1 },
                                        { afbeelding: block.afbeelding2 },
                                    ]}
                                />
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        </div>
    );
}
